// Per-member personalization core (feature 004). The SAFETY GATE: any user-chosen or
// image-derived accent is corrected to meet WCAG AA *before* it can apply (REQ-THEME-001) —
// fun can never break legibility. Pure (hex in, hex out); no DOM, no I/O.
//
// Correction keeps the user's HUE and saturation and only moves LIGHTNESS to the nearest value
// where BOTH hold, against the active theme's surface:
//   • on-accent text (pure black or white, whichever is better) ≥ 4.5:1 vs the accent, and
//   • the accent itself ≥ 3:1 vs the theme surface (so a filled accent is visible on the page).
// Such a lightness always exists (push toward the end opposite the surface), so every input
// yields an AA-safe pair — the property the fuzz test asserts.
import { contrastRatio, hexToRgb, relativeLuminance } from "./contrast";
import { DARK, LIGHT } from "./tokens";

export type Theme = "light" | "dark";
export interface AccentPair {
  accent: string; // corrected accent hex
  onAccent: string; // text/icon colour to use on the accent
}
export interface AccentBoth {
  light: AccentPair;
  dark: AccentPair;
}

const ON_LIGHT = "#ffffff";
const ON_DARK = "#000000";
const SURFACE: Record<Theme, string> = {
  light: LIGHT["color-surface"],
  dark: DARK["color-surface"],
};

// ── hex ↔ HSL ────────────────────────────────────────────────────────────────
function rgbToHsl(r: number, g: number, b: number): [number, number, number] {
  r /= 255;
  g /= 255;
  b /= 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  const d = max - min;
  let h = 0;
  let s = 0;
  if (d !== 0) {
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) h = (g - b) / d + (g < b ? 6 : 0);
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h /= 6;
  }
  return [h, s, l];
}

function hue2rgb(p: number, q: number, t: number): number {
  if (t < 0) t += 1;
  if (t > 1) t -= 1;
  if (t < 1 / 6) return p + (q - p) * 6 * t;
  if (t < 1 / 2) return q;
  if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
  return p;
}

function hslToHex(h: number, s: number, l: number): string {
  let r: number;
  let g: number;
  let b: number;
  if (s === 0) {
    r = g = b = l;
  } else {
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1 / 3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1 / 3);
  }
  const to = (x: number) => Math.round(x * 255).toString(16).padStart(2, "0");
  return `#${to(r)}${to(g)}${to(b)}`;
}

const clamp01 = (x: number) => Math.min(1, Math.max(0, x));
const pickOn = (hex: string): string =>
  contrastRatio(hex, ON_LIGHT) >= contrastRatio(hex, ON_DARK) ? ON_LIGHT : ON_DARK;

/** Correct an accent to an AA-safe {accent, onAccent} for the given theme. Always succeeds. */
export function correctAccent(hex: string, theme: Theme): AccentPair {
  const [h, s, l0] = rgbToHsl(...hexToRgb(hex));
  const surface = SURFACE[theme];
  // search lightness outward from the user's choice for the nearest AA-safe value
  for (let d = 0; d <= 1.0001; d += 0.02) {
    for (const l of [clamp01(l0 - d), clamp01(l0 + d)]) {
      const cand = hslToHex(h, s, l);
      const on = pickOn(cand);
      if (contrastRatio(cand, on) >= 4.5 && contrastRatio(cand, surface) >= 3) {
        return { accent: cand, onAccent: on };
      }
    }
  }
  // unreachable in practice; a guaranteed extreme away from the surface
  const l = relativeLuminance(surface) > 0.5 ? 0.18 : 0.82;
  const cand = hslToHex(h, s, l);
  return { accent: cand, onAccent: pickOn(cand) };
}

/** Correct for both themes (cached in member_prefs.accent_corrected). */
export function correctAccentBoth(hex: string): AccentBoth {
  return { light: correctAccent(hex, "light"), dark: correctAccent(hex, "dark") };
}
